'use client';

import { useEffect, useState } from 'react';
import { useParams } from 'next/navigation';
import { Avatar } from '@/components/ui/AppUI';
import { api, ApiError, User } from '@splace/core';

export default function PublicProfilePage() {
  const params = useParams<{ username: string }>();
  const username = decodeURIComponent(params.username);

  const [profile, setProfile] = useState<User | null>(null);
  const [state, setState] = useState<'loading' | 'ok' | 'not-found' | 'error'>('loading');

  useEffect(() => {
    let alive = true;
    setState('loading');
    api.users.byUsername(username)
      .then(u => { if (alive) { setProfile(u); setState('ok'); } })
      .catch(err => {
        if (!alive) return;
        if (err instanceof ApiError && err.status === 404) setState('not-found');
        else setState('error');
      });
    return () => { alive = false; };
  }, [username]);

  if (state === 'loading') {
    return <Centered>Loading…</Centered>;
  }

  if (state === 'not-found') {
    return (
      <Centered>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>User not found</h1>
        <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--text-muted)' }}>
          <span style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>{`@${username}`}</span>
          {` doesn’t exist, or you’re not linked.`}
        </p>
      </Centered>
    );
  }

  if (state === 'error' || !profile) {
    return <Centered>Something went wrong. Try again.</Centered>;
  }

  const memberSince = new Date(profile.createdAt).toLocaleDateString('en', {
    month: 'short', year: 'numeric',
  });

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      <div style={{
        height: 64, position: 'sticky', top: 0, zIndex: 5,
        background: 'var(--bg)', borderBottom: '1px solid var(--line)',
        display: 'flex', alignItems: 'center', padding: '0 24px',
      }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>
          @{profile.username}
        </h2>
      </div>

      <div style={{ maxWidth: 520, margin: '0 auto', padding: '32px 24px 80px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, marginBottom: 28 }}>
          <Avatar
            src={profile.photoUrl}
            thumbSrc={profile.photoThumbUrl}
            seed={profile.displayName ?? profile.username}
            size={96}
          />
          <div style={{ flex: 1, minWidth: 0, paddingTop: 6 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              {profile.displayName}
            </h1>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
              @{profile.username}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: 28 }}>
          {profile.bio ? (
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: 'var(--text-muted)' }}>
              {profile.bio}
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: 14, color: 'var(--text-dim)', fontStyle: 'italic' }}>
              No bio
            </p>
          )}
        </div>

        <div style={{
          display: 'flex', gap: 32, padding: '18px 0',
          borderTop: '1px solid var(--line-soft)', borderBottom: '1px solid var(--line-soft)',
        }}>
          <Stat label="Member since" value={memberSince} />
        </div>
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14,
    }}>
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 3, fontFamily: 'var(--font-mono)' }}>{label}</div>
    </div>
  );
}
